/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-05 11:29:10
 *
 *******************************************************/
// @flow
import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withI18n } from '@shopify/react-i18n';
import { Button, Icon } from '@shopify/polaris';
import { CancelSmallMinor } from '@shopify/polaris-icons';
import Classnames from 'classnames';
import { change, getFormValues } from 'redux-form';

import ClickOutsideHandler from 'src/components/views/common/clickoutside';
import { RenderRadioButtonItemV2 } from 'src/components/views/common/forms/render-radio-button-item-v2';
import IconCheck from 'src/assets/images/shopify-integration-settings/check.svg?react';
import { MENU_ID, getSubLayoutOptions } from 'src/constants/theme-settings.jsx';
import * as ShopifyIntegrationSettingsAction from 'src/stores/actions/shopify-integration/shopify-integration-settings-actions';
import Emitter, { EVENT_KEY } from 'src/libs/eventEmitter';

type Props = {
  isDifferentFormOrigin: boolean,
  upsertElementActions: (type: string, properties: Record<string, any>) => void
};

type ComposedProps = Props & WithI18nProps;

type State = {};

class ListLayout extends Component<ComposedProps, State> {
  componentDidMount() {
    Emitter.on(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleCloseListLayout);
  }

  componentWillUnmount() {
    Emitter.off(EVENT_KEY.CLICK_IFRAME_VISUAL_EDITOR, this.handleCloseListLayout);
  }

  handleCloseListLayout = () => {
    const { updateFieldReducer } = this.props;
    updateFieldReducer('openListLayout', false);
  };

  handleChangeSubLayout = (value: string) => {
    const { updateField } = this.props;
    updateField(`setting.${MENU_ID.PRODUCT_ITEM}.subLayout`, value);
  };

  render() {
    const { i18n, formValues, templateVersion, boostThemeLib } = this.props;

    const formattedSubLayout = getSubLayoutOptions(templateVersion, boostThemeLib).map(item => ({
      ...item,
      label: i18n.translate(item.label)
    }));

    const productItemSubLayout = _.get(formValues, `setting.${MENU_ID.PRODUCT_ITEM}.subLayout`);

    return (
      <div className="list-layout-container">
        <ClickOutsideHandler onClickOutside={this.handleCloseListLayout} style={{ height: '100%' }}>
          <div className="list-layout-container__header">
            <p className="title">
              {i18n.translate(
                'AdminHTML.ShopifyIntegrationSettings.Form.productCallToAction_ListLayout'
              )}
            </p>
            <Button plain onClick={this.handleCloseListLayout}>
              <Icon source={CancelSmallMinor} />
            </Button>
          </div>

          <div className="list-layout-container__body">
            {formattedSubLayout.map((option, index) => {
              const { label, value } = option;
              return (
                <div key={index} style={{ width: '100%' }}>
                  <RenderRadioButtonItemV2
                    id={value}
                    value={value}
                    input={{
                      name: 'subLayout',
                      value: productItemSubLayout,
                      onChange: this.handleChangeSubLayout
                    }}
                    label={label}
                    helpText={
                      <Sample
                        image={option.sample}
                        isSelected={productItemSubLayout === option.value}
                      />
                    }
                  />
                </div>
              );
            })}
          </div>
        </ClickOutsideHandler>
      </div>
    );
  }
}

function Sample({ image, isSelected }) {
  return (
    <div
      className={Classnames('layout-sample', {
        active: isSelected
      })}>
      {isSelected && <IconCheck className="check" />}
      <img src={image} alt="layout" />
    </div>
  );
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  updateFieldReducer: (fieldName, data) =>
    dispatch(ShopifyIntegrationSettingsAction.handleUpdateField(fieldName, data)),
  updateField: (field, value) =>
    dispatch(change(`ShopifyIntegrationSettingsVisualEditorForm`, field, value))
});

const mapStateToProps = (state, props) => {
  const formValues = getFormValues('ShopifyIntegrationSettingsVisualEditorForm')(state) || {};

  return {
    formValues,
    templateVersion: _.get(state, 'templateInstance.templateInstance.templateVersion', '1.0.0'),
    boostThemeLib: _.get(state, 'shopifyIntegrationSettings.boostThemeLib', 'default')
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ListLayout));
